import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { CategoryTreeItemDto } from '@api/models';
import { RouterLinkWithLang } from '../../../directives/router-link-with-lang/router-link-with-lang.directive';
import { CategoryService } from '../../../pages/category-page/category.service';
import { LanguageService } from '../../../services/language/language.service';
import { IconComponent } from '../../icon/icon.component';

/**
 * The mobile catalog: a full-screen panel opened from the sidebar. Each view is a grid of category
 * cards; a card with subcategories opens them as the next view, slid in on top of the current one,
 * and a leaf goes to its category page. Back closes one view at a time.
 */
@Component({
  selector: 'catalog-panel',
  templateUrl: './catalog-panel.component.html',
  styleUrl: './catalog-panel.component.scss',
  imports: [IconComponent, TranslatePipe, RouterLink, RouterLinkWithLang],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    '(document:keydown.escape)': 'back()',
    '(animationend)': 'onAnimationEnd($event)',
  },
})
export class CatalogPanelComponent {
  protected readonly categoryService = inject(CategoryService);
  private readonly languageService = inject(LanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The panel has slid in and covers the screen: what is under it can be closed unseen. */
  readonly shown = output<void>();
  /** The close button, back or Escape on the top level, or a followed link. */
  readonly closed = output<void>();

  /** The opened categories, outermost first. */
  private readonly opened = signal<CategoryTreeItemDto[]>([]);
  /** The views from the bottom up: the top level (`null`), then one per opened category. */
  protected readonly views = computed(() => [null, ...this.opened()]);

  /**
   * A card with subcategories is a real link to its category page, so it can be opened in a new
   * tab or copied; a plain click opens its view instead.
   */
  protected open(event: MouseEvent, category: CategoryTreeItemDto): void {
    if (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    this.opened.update(opened => [...opened, category]);
  }

  protected back(): void {
    if (this.opened().length) {
      this.opened.update(opened => opened.slice(0, -1));
    } else {
      this.closed.emit();
    }
  }

  protected getHref(category: CategoryTreeItemDto): string {
    return '/' + [this.languageService.getCurrentRouteLang(), category.s].filter(Boolean).join('/');
  }

  protected getImage(image: string): string {
    return image || '/assets/images/no-img.jpg';
  }

  // views opened inside the panel animate too, and their events bubble up here
  protected onAnimationEnd(event: AnimationEvent): void {
    if (event.target === this.host.nativeElement) {
      this.shown.emit();
    }
  }
}
